// The brain's root — TODO T17 (30 Aug 2026); the route table extended at T30 (1 Sep 2026).
//
// Three gates before a screen renders, in this order: Clerk has loaded, there is a session, and
// the BFF recognises that session as one of kadmon's staff. The third is a request rather than a
// claim on the token, because the fact lives in the database and not in Clerk
// (services/bff/README.md § What the gate checks).
//
// THE ROUTE TABLE CARRIES SLOT LINES. Every brain task adds a route, and two brain lanes in
// one round would otherwise collide on the same list. A later lane replaces ONLY its own slot
// line, and git merges non-adjacent hunks clean (design doc § 1.7).
import { useAuth } from '@clerk/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { FORM_ROUTES } from './back-place.ts';
import { ApiError } from '../api/core.ts';
import { useMe } from '../api/me.ts';
import { clearReplies } from '../api/reply-drafts.ts';
// Before the gate and the shell, where the screens' own imports were: the stylesheet's order.
import './screenStyles.ts';
import { NotARecruiterGate, SignInGate } from './Gate.tsx';
import {
  CandidateEntryScreen,
  CandidateScreen,
  CandidatesScreen,
  ClientDocumentScreen,
  ClientScreen,
  ComparisonScreen,
  ConnectionsScreen,
  ContactScreen,
  ConversationScreen,
  ConversationsScreen,
  CrmScreen,
  MeetingReaderScreen,
  MeetingScreen,
  MeetingsScreen,
  ProactiveScreen,
  ProfileScreen,
  ProposedScreen,
  ReportsScreen,
  ResearchRunScreen,
  ResearchScreen,
  SearchScreen,
  SearchesScreen,
  MyListScreen,
  SettingsScreen,
  StaffScreen,
  TalentClientScreen,
  TeamMeetingScreen,
} from './screens.ts';
import { Shell } from './Shell.tsx';

export function App() {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const queryClient = useQueryClient();

  // T296b: a reply kept in memory is the signed-in recruiter's; the next person at this browser
  // must not find it (the page need not reload on a sign-out). T296d: nor on an account switched
  // with no signed-out render between, so it follows the user id; and a sign-out empties the query
  // cache, which nothing is reading then, so it too is gone before the next person signs in.
  useEffect(() => {
    if (isLoaded) clearReplies();
  }, [isLoaded, userId]);
  useEffect(() => {
    if (isLoaded && !isSignedIn) queryClient.clear();
  }, [isLoaded, isSignedIn, queryClient]);

  if (!isLoaded) return <div className="gate" />;
  if (!isSignedIn) return <SignInGate />;
  return <SignedIn />;
}

function SignedIn() {
  const me = useMe();

  // 403 with a valid session: signed in, not linked to a staff row. Its own screen, because it
  // is the state every newly invited recruiter starts in and the fix is somebody else's UPDATE.
  if (me.error instanceof ApiError && me.error.code === 'not_a_recruiter') {
    return <NotARecruiterGate />;
  }

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Navigate to="/searches" replace />} />
        <Route path="searches" element={<SearchesScreen />} />
        <Route path="searches/:searchId" element={<SearchScreen />} />
        {/* T273: Candidates in four. Searches keeps its own address; All is `/candidates`, as it
            was; Proactive and No-man's-land sit beside it. The static paths rank above the id. */}
        <Route path="candidates" element={<CandidatesScreen scope="all" />} />
        <Route path="candidates/proactive" element={<ProactiveScreen />} />
        <Route path="candidates/no-mans-land" element={<CandidatesScreen scope="nomansland" />} />
        <Route path="candidates/:candidateId" element={<CandidateScreen />} />
        {/* The CRM (T58): one company record on one of three tabs, and the company page under
            /crm/clients/:id whichever tab it is on, so a link survives a move. The pre-T58 paths
            redirect — the palette, a bookmark, a colleague's message. */}
        <Route path="crm" element={<Navigate to="/crm/clients" replace />} />
        <Route path="crm/clients" element={<CrmScreen status="client" />} />
        <Route path="crm/potential" element={<CrmScreen status="potential" />} />
        <Route path="crm/sales" element={<CrmScreen status="sales" />} />
        {/* T111: the fourth tab — people from the team's LinkedIn networks, not companies. */}
        <Route path="crm/connections" element={<ConnectionsScreen />} />
        <Route path="crm/clients/talent/:talentClientId" element={<TalentClientScreen />} />
        <Route path="crm/clients/:clientId" element={<ClientScreen />} />
        {/* A client contact's page (T62): the messages kadmon wrote for them, under their company. */}
        <Route path="crm/clients/:clientId/contacts/:contactId" element={<ContactScreen />} />
        <Route path="clients" element={<Navigate to="/crm/clients" replace />} />
        <Route path="clients/:clientId" element={<ClientRedirect />} />
        <Route path="conversations" element={<ConversationsScreen />} />
        {/* T91: everyone kadmon would put forward, across every client, waiting on a decision. */}
        <Route path="proposed" element={<ProposedScreen />} />
        <Route path="conversations/:conversationId" element={<ConversationScreen />} />

        <Route path="reports" element={<ReportsScreen />} />

        {/* Research-vera (T53): every run, and one run's proposals waiting for a decision. */}
        <Route path="research" element={<ResearchScreen />} />
        <Route path="research/:runId" element={<ResearchRunScreen />} />

        {/* Fathom's meetings (T70): one meeting; since T70b five subtabs — Team, Candidates and
            the three CRM tabs — and a team meeting's own page, read through the one privacy rule.
            The bare path lands on Candidates, where Unfiled waits. */}
        <Route path="meetings" element={<Navigate to="/meetings/candidates" replace />} />
        <Route path="meetings/team" element={<MeetingsScreen tab="team" />} />
        <Route path="meetings/team/:teamMeetingId" element={<TeamMeetingScreen />} />
        <Route path="meetings/candidates" element={<MeetingsScreen tab="candidates" />} />
        <Route path="meetings/clients" element={<MeetingsScreen tab="client" />} />
        <Route path="meetings/potential" element={<MeetingsScreen tab="potential" />} />
        <Route path="meetings/sales" element={<MeetingsScreen tab="sales" />} />
        {/* T70d: the recordings the brain could place under nobody, and the demos; T70f the
            meetings with kadmon's lawyer. */}
        <Route path="meetings/investors" element={<MeetingsScreen tab="investors" />} />
        <Route path="meetings/advisors" element={<MeetingsScreen tab="advisors" />} />
        <Route path="meetings/nomads" element={<MeetingsScreen tab="nomads" />} />
        <Route path="meetings/demos" element={<MeetingsScreen tab="demos" />} />
        <Route path="meetings/lawyer" element={<MeetingsScreen tab="lawyer" />} />
        {/* T129: the notes reader, measured against the meetings already filed. */}
        <Route path="meetings/reader" element={<MeetingReaderScreen />} />
        <Route path="meetings/:meetingId" element={<MeetingScreen />} />

        {/* Two or three people on one search, side by side — `?ids=` (T34). */}
        <Route path="searches/:searchId/compare" element={<ComparisonScreen />} />

        {/* The form is a PAGE, not a dialog: too many fields for a modal, and a create is
            navigated to from two places (design doc § T38.3). */}
        <Route path={FORM_ROUTES.new} element={<CandidateEntryScreen />} />
        <Route path={FORM_ROUTES.edit} element={<CandidateEntryScreen />} />

        {/* The profile a client is handed, for one mandate — `?search=` (T42). */}
        <Route path="candidates/:candidateId/profile" element={<ProfileScreen />} />

        {/* T152: a document of profiles for a client, to download or send. */}
        <Route path="client-documents/:documentId" element={<ClientDocumentScreen />} />

        {/* T128: a colleague's profile, a staff thread's Open profile. */}
        <Route path="staff/:staffId" element={<StaffScreen />} />
        <Route path="list" element={<MyListScreen />} />
        <Route path="settings" element={<SettingsScreen />} />
        <Route path="*" element={<Navigate to="/searches" replace />} />
      </Route>
    </Routes>
  );
}

/** `/clients/:id` before T58 is `/crm/clients/:id` since. */
function ClientRedirect() {
  const { clientId = '' } = useParams();
  return <Navigate to={`/crm/clients/${clientId}`} replace />;
}
